import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { i18nLinks, i18nOgMeta, localeUrl } from "@/lib/seo";
import {
  type BudgetBuilderStage,
  raceStagesQueryOptions,
  racesQueryOptions,
  seasonsQueryOptions,
  teamCalculatorQueryOptions,
} from "@/tools/f1-fantasy/hooks/use-f1-data";
import { getNextOrLatestRace } from "@/tools/f1-fantasy/lib/race-utils";
import { BudgetBuilder } from "@/tools/f1-fantasy/pages/BudgetBuilder";

// URL search params — persisted across reloads
const budgetBoostSearchSchema = z.object({
  season: z.coerce.number().optional(),
  race: z.string().optional(),
  stage: z.string().optional(),
  // Presentation: "detailed" (verdict bars, default) or "compact" (table)
  view: z.enum(["detailed", "compact"]).optional(),
});

export const Route = createFileRoute("/f1-fantasy/budget-boost")({
  validateSearch: budgetBoostSearchSchema,
  head: () => ({
    meta: [
      {
        title: "F1 Fantasy Budget Boost — Price Change Predictions | Toolverse",
      },
      {
        name: "description",
        content:
          "Pick drivers whose price will rise and grow your total budget. Data-driven price change predictions before every race weekend.",
      },
      {
        property: "og:title",
        content:
          "F1 Fantasy Budget Boost — Price Change Predictions | Toolverse",
      },
      {
        property: "og:description",
        content:
          "Predict F1 driver price rises and grow your fantasy budget before each race weekend.",
      },
      ...i18nOgMeta("/f1-fantasy/budget-boost"),
    ],
    links: [...i18nLinks("/f1-fantasy/budget-boost")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: localeUrl("/"),
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "F1 Fantasy",
              item: localeUrl("/f1-fantasy"),
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Budget Boost",
              item: localeUrl("/f1-fantasy/budget-boost"),
            },
          ],
        }),
      },
    ],
  }),
  component: BudgetBuilder,
  ssr: true,
  loaderDeps: ({ search }) => ({
    season: search.season,
    race: search.race,
    stage: search.stage,
  }),
  loader: async ({ context, deps }) => {
    const { season: searchSeason, race: searchRace, stage: searchStage } = deps;

    // Step 1: Get current season from DB (seasons ordered DESC, first = latest)
    const seasons = await context.queryClient.ensureQueryData(
      seasonsQueryOptions(),
    );
    const currentSeason = searchSeason ?? seasons?.[0]?.year ?? 2026;

    // Step 2: Fetch races for the current season
    const races = await context.queryClient.ensureQueryData(
      racesQueryOptions(currentSeason),
    );

    const defaultRace = getNextOrLatestRace(races ?? []);

    if (!defaultRace?.slug) {
      return {
        season: currentSeason,
        raceSlug: null,
        stage: "pre_weekend" as BudgetBuilderStage,
      };
    }

    // Prefer race from URL (?race=...), fall back to next upcoming race
    const targetRace = searchRace
      ? (races?.find((r) => r.slug === searchRace) ?? defaultRace)
      : defaultRace;

    // Step 3: Prefetch stages for the target race
    const stagesData = await context.queryClient.ensureQueryData(
      raceStagesQueryOptions(currentSeason, targetRace.slug),
    );

    const defaultStage =
      (stagesData?.currentStage as BudgetBuilderStage) ?? "pre_weekend";

    const currentStage =
      (searchStage as BudgetBuilderStage | undefined) ?? defaultStage;

    // Await team calculator data so the server renders actual content (not a skeleton).
    // prefetchQuery (non-blocking) caused a hydration mismatch: server rendered skeleton
    // but client saw the data in the dehydrated cache and rendered actual content.
    // ensureQueryData blocks SSR until data is ready — server and client both render the
    // same content, eliminating the React #418 hydration error.
    await context.queryClient.ensureQueryData(
      teamCalculatorQueryOptions(currentSeason, targetRace.slug, currentStage),
    );

    return {
      season: currentSeason,
      raceSlug: targetRace.slug,
      stage: currentStage,
    };
  },
});
